import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Availability } from '../../../types';
import { useIsDarkMode } from '../../../hooks/useDarkMode';
import { getHeatColor } from '../../../utils/heatmap';
import { PastSlotOverlay } from '../PastSlotOverlay';
import { formatMonthDayAriaLabel, getMondayWeekdayLabels } from '../../../utils/locale';
import { isDayPast } from '../../../utils/slotTime';

function formatDayKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function buildDayCounts(availability: Availability[]): Map<string, number> {
  const seen = new Map<string, Set<string>>();
  for (const a of availability) {
    if (a?.available && typeof a.day === 'string') {
      let s = seen.get(a.day);
      if (!s) {
        s = new Set();
        seen.set(a.day, s);
      }
      s.add(a.participant);
    }
  }
  const out = new Map<string, number>();
  seen.forEach((s, k) => out.set(k, s.size));
  return out;
}

interface MonthViewProps {
  viewDate: Date;
  availability: Availability[];
  onSelectDay: (date: Date) => void;
}

export function MonthView({ viewDate, availability, onSelectDay }: MonthViewProps) {
  const { t, i18n } = useTranslation();
  const y = viewDate.getFullYear();
  const m = viewDate.getMonth();
  const first = new Date(y, m, 1);
  let sd = first.getDay();
  sd = sd === 0 ? 6 : sd - 1;
  const dim = new Date(y, m + 1, 0).getDate();
  const dipm = new Date(y, m, 0).getDate();
  const todayKey = formatDayKey(stripToday());
  const dark = useIsDarkMode();

  const weekdayLabels = useMemo(
    () => getMondayWeekdayLabels(i18n.language, 'short'),
    [i18n.language]
  );

  const dayCounts = useMemo(() => buildDayCounts(availability), [availability]);

  const cells: { date: Date; outside: boolean }[] = [];
  for (let i = sd - 1; i >= 0; i--) {
    cells.push({ date: new Date(y, m - 1, dipm - i), outside: true });
  }
  for (let day = 1; day <= dim; day++) {
    cells.push({ date: new Date(y, m, day), outside: false });
  }
  const tot = sd + dim;
  const rem = tot % 7;
  const nx = rem === 0 ? 0 : 7 - rem;
  for (let d = 1; d <= nx; d++) {
    cells.push({ date: new Date(y, m + 1, d), outside: true });
  }

  const maxCount = useMemo(() => {
    let max = 0;
    cells.forEach(({ date, outside }) => {
      if (outside) return;
      const c = dayCounts.get(formatDayKey(date)) ?? 0;
      if (c > max) max = c;
    });
    return max;
  }, [cells, dayCounts]);

  return (
    <div className="overflow-hidden rounded-xl border border-room-border">
      <div className="grid grid-cols-7 gap-px bg-room-border">
        {weekdayLabels.map(d => (
          <div
            key={d}
            className="bg-room-surface-2 py-2 text-center text-[9px] font-normal uppercase tracking-widest text-room-text-faint"
          >
            {d}
          </div>
        ))}
        {cells.map(({ date, outside }, idx) => {
          const key = formatDayKey(date);
          const count = outside ? 0 : dayCounts.get(key) ?? 0;
          const isToday = key === todayKey;
          const isPastDay = !outside && isDayPast(key);
          const heat = count > 0 ? getHeatColor(count, Math.max(maxCount, 1), dark) : undefined;
          return (
            <button
              key={`${key}-${idx}`}
              type="button"
              data-zoom-date={key}
              onClick={() => onSelectDay(date)}
              aria-label={formatMonthDayAriaLabel({
                date,
                count: outside ? 0 : count,
                lang: i18n.language,
                t,
              })}
              className={`relative min-h-[76px] p-1.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-room-accent sm:min-h-[76px] ${
                outside
                  ? 'bg-room-surface-2/80'
                  : 'bg-room-surface hover:brightness-95'
              }`}
              style={!outside && heat ? { backgroundColor: heat } : undefined}
            >
              {isPastDay ? <PastSlotOverlay /> : null}
              <div className="relative mb-1 inline-flex">
                <div
                  className={`flex h-[22px] w-[22px] items-center justify-center rounded-full text-xs ${
                    isToday
                      ? 'today-date-mark font-semibold'
                      : outside
                        ? 'text-room-text-faint'
                        : 'text-room-text font-medium'
                  }`}
                >
                  {date.getDate()}
                </div>
                {count > 0 ? (
                  count === 1 ? (
                    <span
                      className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-room-accent ring-2 ring-room-surface"
                      aria-hidden
                    />
                  ) : (
                    <span className="absolute -right-1.5 -top-1 flex h-3.5 min-w-[14px] items-center justify-center rounded-full bg-room-accent px-0.5 text-[8px] font-bold leading-none text-[#06231D] ring-2 ring-room-surface">
                      {count}
                    </span>
                  )
                ) : null}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function stripToday(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}
